import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Printer, Send } from "lucide-react";
import {
  buildContractorPayPeriod, getContractorPayPeriod, setPayPeriodStatus,
} from "@/lib/contractors.functions";
import { PAY_PERIOD_STATUS_LABEL, type PayPeriodStatus } from "@/lib/contractors";
import { useContractorWorkspace } from "./contractor.index";

export const Route = createFileRoute("/_authenticated/contractor/pay-periods")({
  component: PayPeriodsPage,
  validateSearch: (search: Record<string, unknown>) => ({
    id: typeof search["id"] === "string" ? (search["id"] as string) : undefined,
  }),
  head: () => ({
    meta: [
      { title: "Contractor pay periods — MissionReady" },
      { name: "description", content: "Review contracted hours by pay period, export a PDF, or submit the timesheet to the inviting organization." },
      { property: "og:title", content: "Contractor pay periods — MissionReady" },
      { property: "og:description", content: "Pay period review, PDF export and timesheet submission for contracted work." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
    ],
  }),
});

function PayPeriodsPage() {
  const { id } = Route.useSearch();
  const { user } = useAuth();
  const qc = useQueryClient();
  const { data: ws } = useContractorWorkspace();
  const buildFn = useServerFn(buildContractorPayPeriod);
  const getFn = useServerFn(getContractorPayPeriod);
  const statusFn = useServerFn(setPayPeriodStatus);

  const periods: any[] = ws?.payPeriods ?? [];
  const partnerships: any[] = ws?.partnerships ?? [];
  const memberships: any[] = ws?.memberships ?? [];
  const isOwner = memberships.some((m) => m.contractor_role === "contractor_owner");
  const primary = partnerships[0];

  const [openId, setOpenId] = useState<string | undefined>(id);
  useEffect(() => { if (id) setOpenId(id); }, [id]);
  const [form, setForm] = useState({ start: "", end: "" });
  const [busy, setBusy] = useState<string | null>(null);

  const { data: detail } = useQuery({
    queryKey: ["contractor-pay-period", openId],
    enabled: Boolean(openId),
    queryFn: () => getFn({ data: { payPeriodId: openId! } }),
  });

  const refresh = async () => {
    await qc.invalidateQueries({ queryKey: ["contractor-workspace", user?.id ?? "anon"] });
    if (openId) await qc.invalidateQueries({ queryKey: ["contractor-pay-period", openId] });
  };

  const act = async (label: string, fn: () => Promise<unknown>) => {
    setBusy(label);
    try { await fn(); await refresh(); }
    catch (err: any) { toast.error(typeof err?.message === "string" ? err.message : "Action failed"); }
    finally { setBusy(null); }
  };

  const items: any[] = detail?.items ?? [];
  const period: any = detail?.period;

  return (
    <div className="mx-auto max-w-3xl px-4 pb-28 pt-4 sm:pt-6">
      <Link to="/contractor" className="mb-3 inline-flex min-h-10 items-center gap-1 text-sm text-muted-foreground hover:text-foreground print:hidden">
        <ArrowLeft className="h-4 w-4" aria-hidden /> Contracted work
      </Link>
      <h1 className="font-display text-2xl font-semibold">Pay periods</h1>
      <p className="mt-1 text-sm text-muted-foreground print:hidden">
        Hours come from the time clock records for contracted shifts. MissionReady does not process payments.
      </p>

      {isOwner && primary ? (
        <div className="mt-4 flex flex-wrap items-end gap-2 print:hidden">
          <div className="space-y-1.5">
            <Label htmlFor="pp-from">From</Label>
            <Input id="pp-from" type="date" className="min-h-11" value={form.start}
              onChange={(e) => setForm({ ...form, start: e.target.value })} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pp-to">To</Label>
            <Input id="pp-to" type="date" className="min-h-11" value={form.end}
              onChange={(e) => setForm({ ...form, end: e.target.value })} />
          </div>
          <Button className="min-h-11" disabled={busy === "build"}
            onClick={() => {
              if (!form.start || !form.end) return toast.error("Choose both dates");
              void act("build", async () => {
                const res: any = await buildFn({ data: { partnershipId: primary.id, periodStart: form.start, periodEnd: form.end } });
                setOpenId(res.payPeriodId);
                toast.success("Pay period built");
              });
            }}>
            Review pay period
          </Button>
        </div>
      ) : null}

      <ul className="mt-5 space-y-2 print:hidden">
        {periods.length === 0 ? (
          <li className="text-sm text-muted-foreground">No pay periods yet.</li>
        ) : periods.map((pp) => (
          <li key={pp.id}>
            <button
              onClick={() => setOpenId(pp.id)}
              className={`w-full rounded-lg border p-3 text-left ${openId === pp.id ? "border-primary" : "border-border"}`}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-medium">{pp.period_start} → {pp.period_end}</span>
                <span className="text-xs text-muted-foreground">
                  {Number(pp.total_hours).toFixed(2)} h · {PAY_PERIOD_STATUS_LABEL[pp.status as PayPeriodStatus] ?? pp.status}
                </span>
              </div>
            </button>
          </li>
        ))}
      </ul>

      {period ? (
        <section className="mt-6 rounded-xl border border-border bg-card p-4" aria-labelledby="pp-detail">
          <header>
            <h2 id="pp-detail" className="font-display text-lg font-semibold">
              {detail?.partnership?.contractor_name} · {period.period_start} → {period.period_end}
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              For {detail?.partnership?.inviting_org_name ?? "the inviting organization"} ·{" "}
              {Number(period.total_hours).toFixed(2)} hours ·{" "}
              {PAY_PERIOD_STATUS_LABEL[period.status as PayPeriodStatus] ?? period.status}
            </p>
          </header>

          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-left text-xs">
              <caption className="sr-only">Contracted shifts, punches and hours for this pay period</caption>
              <thead>
                <tr className="border-b border-border text-muted-foreground">
                  <th scope="col" className="py-2 pr-2">Date</th>
                  <th scope="col" className="py-2 pr-2">Site</th>
                  <th scope="col" className="py-2 pr-2">Member</th>
                  <th scope="col" className="py-2 pr-2">Clock in</th>
                  <th scope="col" className="py-2 pr-2">Clock out</th>
                  <th scope="col" className="py-2">Hours</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr><td colSpan={6} className="py-3 text-muted-foreground">No contracted shifts in this period.</td></tr>
                ) : items.map((it) => (
                  <tr key={it.id} className="border-b border-border/50">
                    <td className="py-2 pr-2">{it.shift_starts_at ? new Date(it.shift_starts_at).toLocaleDateString() : "—"}</td>
                    <td className="py-2 pr-2">{it.siteName ?? "—"}</td>
                    <td className="py-2 pr-2">{it.memberName}</td>
                    <td className="py-2 pr-2">{it.clock_in_at ? new Date(it.clock_in_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—"}</td>
                    <td className="py-2 pr-2">{it.clock_out_at ? new Date(it.clock_out_at).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }) : "—"}</td>
                    <td className="py-2 font-semibold">{Number(it.hours).toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={5} className="py-2 text-right">Total</th>
                  <td className="py-2 font-semibold">{Number(period.total_hours).toFixed(2)}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <div className="mt-4 flex flex-wrap gap-2 print:hidden">
            <Button variant="outline" className="min-h-12" onClick={() => window.print()}>
              <Printer className="mr-2 h-4 w-4" aria-hidden /> Export PDF / print
            </Button>
            {isOwner && (period.status === "ready_for_review" || period.status === "returned") ? (
              <Button className="min-h-12" disabled={busy === "verify"}
                onClick={() => act("verify", () => statusFn({ data: { payPeriodId: period.id, status: "verified" } }))}>
                Mark verified
              </Button>
            ) : null}
            {isOwner && period.status === "verified" ? (
              <Button className="min-h-12" disabled={busy === "submit"}
                onClick={() => act("submit", async () => {
                  await statusFn({ data: { payPeriodId: period.id, status: "submitted" } });
                  toast.success("Submitted to the inviting organization");
                })}>
                <Send className="mr-2 h-4 w-4" aria-hidden /> Submit to inviting organization
              </Button>
            ) : null}
          </div>
          {period.notes ? <p className="mt-3 text-xs text-muted-foreground">Note: {period.notes}</p> : null}
        </section>
      ) : null}
    </div>
  );
}
